import React, { useEffect, useRef, useState } from "react";

import CloseIcon from "images/icons/close-outline.inline.svg";

import ContactForm from "../../components/forms/contact";
import Newsletter from "../../components/newsletter";
import ProgramInfo from "../../components/program/ingo/programInfo";
import ProgramSummary from "../../components/program/programSummary";
import RelatedPrograms from "../../components/program/relatedPrograms";
import TravelFlexibility from "../../components/program/travelFlexibility";
import EnquireCard from "../../components/shared/enquireCard";
import MobileGallery from "../../components/shared/gallery/mobileGallery";
import Hero from "../../components/shared/hero";
import Modal from "../../components/shared/modal";
import ModalInPortal from "../../components/shared/modals/modalInPortal";
import { useTheme } from "../../context/ThemeContext";
import { useProgramFees } from "../../hooks/useProgramFees";
import { useProgramTestimonials } from "../../hooks/useProgramTestimonials";
import { flattenMenu } from "../../utils/flattenMenu";
import {
  formatHeroBackgroundOptions,
  shortcodePreProcessor
} from "../../utils/formatContent";
import ProgramAccommodation from "./ingo/programAccommodation";
import ProgramDateDurations from "./ingo/programDateDurations";
import ProgramTestimonials from "./ingo/programTestimonials";

const IngoProgramPageComponent = ({ data, location }) => {
  const { currentSiteMeta } = useTheme();
  const containerRef = useRef(null);
  const stickyRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const toppoint = containerRef.current?.getBoundingClientRect().top;
      const bottompoint = containerRef.current?.getBoundingClientRect().bottom;
      const cardTop = (window.innerHeight - 410) / 2;
      const cardHeight = 410;
      if (stickyRef.current) {
        if (toppoint > cardTop) {
          stickyRef.current.style.top = 0;
          stickyRef.current.style.bottom = "auto";
        } else if (bottompoint < cardHeight + cardTop) {
          stickyRef.current.style.top = "auto";
          stickyRef.current.style.bottom = 0;
        } else {
          stickyRef.current.style.top = cardTop - toppoint + "px";
          stickyRef.current.style.bottom = "auto";
        }
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyRef, containerRef]);

  const [openParentModal, setOpenParentModal] = useState(false);
  const [openBrochureDownloadModal, setBrochureDownloadModal] = useState(false);
  const [getActiveTabSlidebySlug, setActiveTabSlidebySlug] = useState(null);

  const program = data?.program ?? {};
  const programDetails = program?.acfProgramsMeta ?? {};
  const programGeographicals = program?.geographical ?? [];
  const sharedDetails = data?.genericDetails ?? [];
  const ingoPrograms = sharedDetails?.ingoPrograms?.ingoPrograms;
  const programName = program?.title ?? "";
  const programId = program?.databaseId ?? "";
  const programCode = programDetails?.programCode ?? programId;
  const ingoName = programDetails?.ingoBioName ?? "";

  const programIdentifier = programCode ?? programId;
  const programSlug = program?.slug ?? program?.uri;
  const programSuffix = /[a-zA-Z]/.test(programIdentifier)
    ? ["program_code", programIdentifier].join("=")
    : ["program_id", programIdentifier].join("=");

  const applyNow = programId
    ? `/application-form/?${programSuffix}&flex_program&flex_name=${ingoName}`
    : `/application-form/?flex_program&flex_name=${ingoName}`;
  const enquireNow = programId
    ? `/enquiry-form/?type=specific_enquiry&program=${programId}&program_title=${programName}&flex_program`
    : "/enquiry-form/?flex_program";
  const programTitle = programDetails?.programTitle2 ?? "";
  const programPopular = programDetails?.programPopular ?? false;
  const programCapacity = programDetails?.programCapacity ?? "";
  const programDuration = programDetails?.durationDisplay ?? "";
  const programFocus = programDetails?.programFocuses ?? [];
  const programFeaturedVideo = programDetails?.projectVideo ?? "";
  const programFeaturedVideoTestimonials =
    programDetails?.programFeaturedVideoTestimonials ?? "";
  const accommodationGallery = programDetails?.ingoAccommodationGallery ?? [];
  const accommodationSectionDesc =
    programDetails?.ingoAccommodationSectionDesc ?? "";
  const accommodationSectionTitle =
    programDetails?.ingoAccommodationSectionTitle ?? "";
  const programHeading =
    programDetails?.programHeaderOverlayTitle ?? "Emotive headline here";

  const mobileVideoGallery = [];
  if (
    programFeaturedVideoTestimonials &&
    programFeaturedVideoTestimonials.length > 0
  ) {
    programFeaturedVideoTestimonials.forEach((videos) => {
      mobileVideoGallery.push({
        genVideoUrl: videos?.programFeaturedVideo
      });
    });
  }

  const heroOpts = formatHeroBackgroundOptions(programDetails);

  const programFeaturedImage = heroOpts?.image?.desktop;
  const programFeaturedMobileImage = heroOpts?.image?.mobile;
  const programGallery =
    programDetails?.gallery ?? program?.gallery?.nodes ?? [];

  const mobileFilterGalleryCollection = programGallery?.filter(
    (item) =>
      !(item.localFile?.extension === "svg") &&
      !(item.localFile?.extension === "")
  );

  const filterGalleryCollection = mobileFilterGalleryCollection?.filter(
    (item) => item.id !== programFeaturedImage.id
  );

  const filteredGallery = programFeaturedVideo
    ? filterGalleryCollection
    : filterGalleryCollection.slice(1);

  const programGalleryFeaturedImage =
    filterGalleryCollection[0].sourceUrl ??
    program?.featuredImage?.node?.sourceUrl ??
    "";

  const programDatabaseID = program.databaseId;
  const programTestimonials =
    useProgramTestimonials({ programId: programDatabaseID }) ?? [];

  const programTypes = programDetails.programTypes?.flatMap(
    (item) => item.slug
  );

  const programTypesFull = programDetails.programTypes;

  const displayProgramType =
    programTypesFull
      ?.flatMap((item) => item.title)
      ?.shift()
      ?.split(" ")
      ?.join(" <br/>") || "";

  const flatGeoArr = programGeographicals?.nodes.map((geo) => geo);
  const nestedGeo = flattenMenu(flatGeoArr, {
    idKey: "id",
    parentKey: "parentId",
    childrenKey: "children"
  });
  const programHub = nestedGeo
    ?.flatMap((item) => item?.children?.flatMap((child) => child?.children))
    .shift();
  const geographicalNavLinks = [];
  if (nestedGeo && nestedGeo.length) {
    nestedGeo.forEach((geographical, index) => {
      geographicalNavLinks.push({
        imgPath: "location-icon.svg",
        toPage: geographical?.geographicalTaxonomy?.pageLink?.uri ?? "#",
        text: `GVI ${geographical.name}`
      });

      geographical.children.forEach((childG) => {
        geographicalNavLinks.push({
          imgPath: null,
          toPage: childG?.geographicalTaxonomy?.pageLink?.uri ?? "#",
          text: childG.name
        });

        childG.children.forEach((gChild) => {
          geographicalNavLinks.push({
            imgPath: null,
            toPage: gChild?.geographicalTaxonomy?.pageLink?.uri ?? "#",
            text: gChild.name
          });
        });
      });
    });
  }

  const relatedGeoSlugs = flatGeoArr?.flatMap((geo) => geo.slug);

  const heroNavLinks = geographicalNavLinks;

  const programUri = program.databaseId;
  const keyMessageIcons =
    sharedDetails?.globalProgramConfig?.acfKeyMessagingIcons?.kmiPrograms ?? [];
  const experiences =
    programHub?.acfCountryExploration?.gviExperiences?.filter((item) => item) ??
    [];

  const { programImpactTitle, programImpactList } =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? {};

  const {
    ingoGlobalSupportFeeDesc,
    sidebarApplicationWidget,
    sidebarBookWithConfidenceWidget
  } = ingoPrograms;

  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const currencyPriceFilter = program.acfProgramsMeta?.minimumPriceFilter?.find(
    (filter) =>
      filter?.maxPriceFilterCurrencyIsoCode?.toLowerCase() === currencyToUse
  );
  const priceFilterAmount =
    currencyPriceFilter?.maxPriceFilterDailyAmount ?? "";
  const programPrice =
    `${
      currentSiteMeta?.currency_symbol ??
      process.env.GATSBY_PROJECT_CURRENCY ??
      ""
    }${priceFilterAmount}` ?? "";

  const enquireCardDesc =
    sidebarApplicationWidget?.ingoExclusiveOfferDesc ??
    `Make it happen. Join this program from SHORTCODE_FlexDailyPrice`?.replace(
      "SHORTCODE_FlexDailyPrice",
      [programPrice, " per day."].join("")
    );
  const enquireCardDetails = {
    cardTitle: sidebarApplicationWidget?.ingoExclusiveOfferTitle ?? "",
    enquireCardImg: sidebarApplicationWidget?.exclusiveOfferImage,
    cardDesc: enquireCardDesc,
    cardBookDesc: ""
  };

  const travelDetails = {
    title:
      sidebarBookWithConfidenceWidget?.ingoBookWithConfidenceTitle ??
      "Why book with GVI",
    description: "",
    items: sidebarBookWithConfidenceWidget?.bookWithConfidenceList?.map(
      (item) => {
        return item?.ingoListItem ?? "";
      }
    )
  };

  const programInclusions = {
    items: [
      {
        title: "Fully Comprehensive Insurance, including",
        items: [
          "Cover for medical expenses",
          "Cover for trip interruption",
          "Personal stuff, like you phone or camera",
          "24/7 emergency assistance"
        ]
      },
      {
        title: "Flexible cancellation"
      },
      {
        title: "Free booking changes up to 14 days"
      },
      {
        title: "24/7 mental health support"
      },
      {
        title: "Program ethics and impact assurance"
      },
      {
        title: "Your own dedicated trip manager"
      }
    ]
  }; //TODO acf

  const impact = programImpactList?.map((item) => {
    return item?.impact;
  });

  const programImpact = {
    title: programImpactTitle ?? "Program impact",
    description: impact ?? []
  };

  const liveUpdates = programHub?.geographicalTaxonomy ?? "";

  const countryExploration =
    sharedDetails.countryExploration &&
    sharedDetails.countryExploration.acfCountryExploration
      ? sharedDetails.countryExploration.acfCountryExploration
      : "";
  const expTabTitle =
    countryExploration?.gviExperiencesLabel ??
    "GVI experiences included in your program, <span> at no extra cost</span>";

  const genericConfig =
    sharedDetails?.globalProgramConfig?.acfGenericGlobalConfig ?? [];

  const parentFormType = "parent_request";
  const parentFormHeading =
    genericConfig?.parentConsultationHeading ?? "Free parent consultation";
  const parentFormSubheading =
    genericConfig?.parentConsultationSubheading ?? "";

  const locationPages = programHub?.slug ?? "";

  const brochureDetails = ingoPrograms?.ingoBrochures || [];

  //filter the brochures collection to the program location
  const brochureCollection = brochureDetails?.filter((item) => {
    return item?.brochureGeographical?.some((geo) =>
      locationPages?.includes(geo.slug)
    );
  });

  const brochureDownloadFile =
    (brochureCollection?.length > 0
      ? brochureCollection[0]?.brochureFile?.mediaItemUrl
      : null) || null;

  const ingoProviderBio = {
    name: programDetails?.ingoBioName ?? "",
    desc: programDetails?.ingoBioDesc ?? "",
    avatar:
      programDetails?.ingoBioAvatar?.localFile?.publicURL ??
      programDetails?.ingoBioAvatar?.sourceUrl,
    experience: programDetails?.ingoBioExperience ?? ""
  };

  const { ingoPriceDurationsSectionTitle, ingoPriceDurationsSectionSubtext } =
    programDetails ?? {};

  const sdgIcons =
    program?.taxonomySDGIcon?.nodes?.map((items) => {
      return {
        title: items?.name ?? "test",
        icon: items?.sdgIconcs?.sdgIcon ?? ""
      };
    }) || [];

  const programFees = useProgramFees() || [];

  const programPriceDescription = shortcodePreProcessor({
    programFees,
    currentSiteMeta,
    description:
      ingoGlobalSupportFeeDesc ||
      "<span>Secure your spot with a SHORTCODE_INGOSupportFee deposit</span> and enjoy interest-free instalments on the remaining fee."
  });

  const brochureDownloadFormHeading = "Enter your details <br/> to download";
  const brochureDownloadFormType = "ingo_brochure_download";
  const brochureDownloadFormHiddenFields = [
    { fieldName: "program_id", fieldValue: programCode }
  ];

  const handleCloseModal = () => {
    setBrochureDownloadModal(false);
  };

  return (
    <div className={"dark:gvi-dark-gray-bg bg-primary-bg"}>
      <Hero
        navLinks={heroNavLinks}
        heroHeadLine={programHeading}
        heroImg={programFeaturedImage}
        heroClass="hero-section flex-col hidden sm:flex"
        subtextTitle={displayProgramType}
        heroImageMobile={programFeaturedMobileImage}
      />
      <MobileGallery
        containerClass={
          "ingo-mobile-gallery mobile-gallery-section relative bg-transparent h-56 w-full sm:hidden"
        }
        gallery={programGallery}
        featuredSingleVideo={programFeaturedVideo}
        videoGallery={mobileVideoGallery}
        programGalleryFeaturedImage={""}
      />
      <ProgramSummary
        programName={programName}
        programTitle={programTitle}
        programDuration={programDuration}
        programFocus={programFocus}
        programFeaturedVideo={programFeaturedVideo}
        programFeaturedImage={programFeaturedImage}
        programGalleryFeaturedImage={programGalleryFeaturedImage}
        programProviderBio={ingoProviderBio}
        programUri={programUri}
        programId={programId}
        programCode={programCode}
        gallery={filteredGallery}
        programPopular={programPopular}
        programCapacity={programCapacity}
        programLocation={geographicalNavLinks}
        programFeaturedVideoTestimonials={programFeaturedVideoTestimonials}
        programType={programTypes}
        programTypes={programDetails.programTypes}
        setOpenModal={setOpenParentModal}
        openModal={openParentModal}
        formType={parentFormType}
        formSubheading={parentFormSubheading}
        formHeading={parentFormHeading}
        setActiveTabSlidebySlug={setActiveTabSlidebySlug}
        sdgIcons={sdgIcons}
        galleryCTALabel={"See gallery"}
        layout={"ingo"}
        containerClass={
          "summary-section flex flex-col items-center justify-center bg-generic-bg mx-auto pb-18 pt-8 px-2 relative"
        }
        innerDetailContainerClass={
          "inner-detail-container justify-between md:items-center mt-4 pt-3 sm:my-4 sm:pb-8 sm:py-0 border-t border-gray-generic dark:border-gray-standard sm:border-0"
        }
        bgIllustrationClass={
          "absolute z-0 hidden -bottom-80 -left-16 md:block stroke-white w-1/3"
        }
        innerContainer={"container flex flex-col w-11/12 sm:w-10/12"}
        mainHeadingClass={"font-normal sm:dark:font-semibold"}
        subHeadingClass={"py-2 font-light leading-6 sm:leading-[38px]"}
        galleryContainerClass={
          "hidden sm:grid grid-flow-col grid-cols-4 grid-rows-2 gap-0 program-gallery sm:h-96 sm:gap-4"
        }
        galleryViewMoreContainerClass={
          "hidden sm:flex flex-row justify-end py-4 sm:absolute sm:bottom-3 sm:right-3"
        }
        featuredVideoClass={"bg-gray-dark"}
        featuredGalleryItemClass={"dark:bg-gray-dark"}
      />
      <div className="relative overflow-x-hidden sticky-wrapper sm:overflow-x-clip ">
        <ProgramInfo
          programTitle={programName}
          programDuration={programDuration}
          programDetails={programDetails}
          providerBiographyDetail={ingoProviderBio}
          programSDGIcons={sdgIcons}
          excerpt={program?.content}
          programLocation={geographicalNavLinks}
          sharedDetails={sharedDetails}
          testimonials={programTestimonials}
          program={program}
          keyMessageIcons={keyMessageIcons}
          experiences={experiences}
          expTabTitle={expTabTitle}
          programHub={programHub}
          programPopular={programPopular}
          programCapacity={programCapacity}
          programImpact={programImpact}
          travelDetails={travelDetails}
          location={location}
          liveUpdates={liveUpdates}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          setBrochureDownloadModal={setBrochureDownloadModal}
          brochureDownloadFile={brochureDownloadFile}
          innerContainerClass="summary-section justify-center w-11/12 pt-12 sm:pb-5 flex gap-10 flex-wrap sm:flex-nowrap sm:w-10/12 2xl:container 2xl:m-auto"
          outerContainerClass="w-full bg-primary-bg mx-auto flex items-center justify-center relative overflow-hidden"
        />

        <ProgramAccommodation
          gallery={accommodationGallery}
          description={accommodationSectionDesc}
          title={accommodationSectionTitle}
        />

        <ProgramDateDurations
          programCode={programCode}
          title={ingoPriceDurationsSectionTitle}
          travelDesc={ingoPriceDurationsSectionSubtext}
          description={programPriceDescription}
          applicationUrl={applyNow}
          programInclusions={programInclusions}
        />

        <div className="hidden w-full md:absolute md:h-full md:sticky-position-programs md:w-auto md:flex">
          <div className="relative w-side-sticky-widget" ref={containerRef}>
            <div ref={stickyRef} className={`card-sticky-container absolute`}>
              <EnquireCard
                applyNow={applyNow}
                enquireNow={enquireNow}
                enquireCardDetails={enquireCardDetails}
                isGVI={false}
                descClass={
                  "mb-4 text-xl font-alice font-light text-left text-generic-text dark:text-xl dark:font-light dark:font-body hover:text-gray-900 leading-6"
                }
              />

              <TravelFlexibility
                travelDetails={travelDetails}
                isGVI={false}
                hasBullets={true}
                outerContainerClass="mt-8 border dark:border-0.5 !border-gray-generic dark:border-black-quaternary 2xl:w-[337px]"
                innerContainerClass="px-8 py-6 bg-secondary-bg dark:xxs:bg-[#3d3d3d]"
                headingClass="text-xl font-alice text-black-lighter dark:font-normal dark:font-semibold dark:font-body dark:xxs:text-white pb-3"
                listItemClass="text-xs text-generic-text dark:text-white"
                iconClass="w-4 sm:w-3 h-4 sm:h-3 fill-black-lighter dark:fill-white"
              />
            </div>
          </div>
        </div>
      </div>

      <ProgramTestimonials TestimonialProgramCode={programCode} />

      <RelatedPrograms
        relatedGeoSlugs={relatedGeoSlugs}
        innerContainerClass={"sm:bg-white sm:dark:bg-gray-standard"}
        excludeSlug={programSlug}
        isFlexProgram={true}
        program={program}
      />

      <Newsletter headerCol="sm:bg-gray-tertiary dark:sm:bg-gray-standard" />

      {openBrochureDownloadModal && (
        <>
          <ModalInPortal
            openModal={openBrochureDownloadModal}
            modal={
              <Modal
                closeModal={handleCloseModal}
                modalCloseIcon={
                  <CloseIcon className="h-5 fill-white dark:fill-white" />
                }
                modalContentStyles="w-full mx-auto bg-yellow-standard md:blur-none dark:gvi-blur-newsletter-bg dark:gvi-blur-bg gvi-border-light border-0.5 border-gray-testimonial rounded-xxs py-3 z-10"
                modalContainerStyles="w-full sm:w-3/5 h-full sm:h-auto"
                modalOuterContainerStyles={
                  "fixed top-0 left-0 right-0 overflow-scroll z-20 flex items-center justify-center w-full h-full sm:modalBackground bg-white dark:bg-gray-darkest sm:gvi-modal-light-bg dark:sm:bg-gray-darker/80"
                }
                modalCloseBtnStyles="sm:hidden px-4 pt-4 sm:hidden"
                modalContent={
                  <div className="px-4 sm:px-16 pt-4 pb-8">
                    <ContactForm
                      formType={brochureDownloadFormType}
                      formHeading={brochureDownloadFormHeading}
                      formStyles="rounded-md w-full mx-auto"
                      ctaLabel="Download"
                      hiddenFields={brochureDownloadFormHiddenFields}
                      programItineraryFile={brochureDownloadFile}
                      downloadTitle={"Brochure"}
                      formTooltipIconStyles={"fill-white"}
                      buttonClass={
                        "w-full sm:w-1/2 callback-button bg-white dark:gvi-bg-green dark:hover:gvi-bg-green dark:!text-gray-darker dark:border-green-standard dark:hover:border-green-standard"
                      }
                      iconColor={"fill-white stroke-white"}
                      calendlyActive={false}
                      customFormType={"journeys_brochures_form"}
                    />
                  </div>
                }
              />
            }
          ></ModalInPortal>
        </>
      )}
    </div>
  );
};

export default IngoProgramPageComponent;
